/**
 * Concise dashboard quick-actions per role — aligned with menuAccess.js gates.
 * Only high-frequency destinations; admin/low-use items stay in the sidebar.
 */
import { isPathAllowedForRole } from './menuAccess';

const SHORTCUTS_BY_ROLE = {
  simple_user: [
    { id: 'new-ticket', label: 'تیکت جدید', path: '/tickets/new', icon: '📝', primary: true },
    { id: 'smart-help', label: 'راهنمای هوشمند', path: '/help', icon: '💬' },
    { id: 'my-tickets', label: 'تیکت‌های من', path: '/dashboard', icon: '🎫', action: 'filter:all' },
    { id: 'pending', label: 'بدون پاسخ', path: '/dashboard', icon: '⏳', action: 'filter:noresp' },
    { id: 'profile', label: 'پروفایل', path: '/profile/edit', icon: '👤' },
  ],
  manager_user: [
    { id: 'new-ticket', label: 'تیکت جدید', path: '/tickets/new', icon: '📝', primary: true },
    { id: 'smart-help', label: 'راهنمای هوشمند', path: '/help', icon: '💬' },
    { id: 'my-tickets', label: 'تیکت‌ها', path: '/dashboard', icon: '🎫', action: 'filter:all' },
    { id: 'pending', label: 'بدون پاسخ', path: '/dashboard', icon: '⏳', action: 'filter:noresp' },
    { id: 'add-user', label: 'افزودن کاربر', path: '/users/add/company', icon: '➕' },
    { id: 'users', label: 'کاربران', path: '/users', icon: '👥' },
  ],
  guest_user: [
    { id: 'new-ticket', label: 'تیکت جدید', path: '/tickets/new', icon: '📝', primary: true },
    { id: 'smart-help', label: 'راهنمای هوشمند', path: '/help', icon: '💬' },
    { id: 'my-tickets', label: 'تیکت‌های من', path: '/dashboard', icon: '🎫', action: 'filter:all' },
    { id: 'pending', label: 'بدون پاسخ', path: '/dashboard', icon: '⏳', action: 'filter:noresp' },
  ],
  simple_employee: [
    { id: 'tickets', label: 'تیکت‌ها', path: '/dashboard', icon: '🎫', action: 'section:tickets' },
    { id: 'missions', label: 'ماموریت‌ها', path: '/missions/my', icon: '🚗', action: 'section:missions' },
    { id: 'daily', label: 'گزارش روزانه', path: '/reports/daily', icon: '📋' },
  ],
  manager_employee: [
    { id: 'tickets', label: 'تیکت‌ها', path: '/dashboard', icon: '🎫', action: 'section:tickets' },
    { id: 'referred', label: 'ارجاع‌شده', path: '/tickets/referred', icon: '📤' },
    { id: 'daily', label: 'گزارش روزانه', path: '/reports/daily', icon: '📋' },
    { id: 'missions', label: 'ماموریت‌ها', path: '/missions/define', icon: '🚗' },
    { id: 'ai', label: 'هوش مصنوعی', path: '/ai/manager', icon: '🤖' },
  ],
  dispatcher: [
    { id: 'queue', label: 'صف دیسپچر', path: '/dispatcher', icon: '📥' },
    { id: 'assigned', label: 'ارجاع‌شده', path: '/dispatcher/assigned', icon: '⏳' },
    { id: 'ai-routing', label: 'ارجاع با AI', path: '/dispatcher/ai-routing', icon: 'ai' },
    { id: 'companies', label: 'شرکت‌ها', path: '/companies', icon: '🏢' },
    { id: 'users', label: 'کاربران', path: '/users', icon: '👥' },
  ],
};

/** Customer ticket filters — 3 high-signal tabs instead of 5. */
export const CUSTOMER_FILTERS = [
  { id: 'all', label: 'همه', color: '#3b82f6' },
  { id: 'noresp', label: 'بدون پاسخ', color: '#f43f5e' },
  { id: 'active', label: 'فعال', color: '#8b5cf6' },
];

/** Employee ticket filters — shorter labels. */
export const EMPLOYEE_DASHBOARD_FILTERS = [
  { id: 'unseen', label: 'جدید', color: '#f59e0b' },
  { id: 'seen_unanswered', label: 'در انتظار', color: '#f43f5e' },
  { id: 'answered', label: 'پاسخ‌داده', color: '#10b981' },
  { id: 'closed', label: 'خاتمه یافته', color: '#17202A' },
  { id: 'all', label: 'همه', color: '#29AAE1' },
];

export function getDashboardShortcuts(role) {
  const list = SHORTCUTS_BY_ROLE[role] || SHORTCUTS_BY_ROLE.simple_user;
  return list.filter((item) => isPathAllowedForRole(item.path, role));
}

/** Document before/after for deploy reports. */
export const SHORTCUT_SUMMARY_BEFORE = {
  simple_user: 'هدر: + تیکت جدید | فیلتر: همه، دیده‌نشده، بدون پاسخ، دیده‌شده، فعال (۵) | جستجو، مرتب‌سازی',
  manager_user: 'همان simple_user + بدون میانبر مدیریت کاربر',
  guest_user: 'همان simple_user',
  simple_employee: 'فیلتر: دیده‌نشده، دیده‌شده بدون پاسخ، پاسخ داده‌شده | تب: تیکت‌ها/ماموریت‌ها | فیلتر شرکت، جستجو',
  manager_employee: 'همان employee + بدون میانبر سریع (AI/ارجاع در سایدبار)',
  dispatcher: 'فقط لیست صف — بدون میانبر داشبورد',
};

export const SHORTCUT_SUMMARY_AFTER = {
  simple_user: 'میانبر: تیکت جدید، تیکت‌های من، بدون پاسخ، پروفایل | فیلتر: همه، بدون پاسخ، فعال (۳)',
  manager_user: 'میانبر: تیکت جدید، تیکت‌ها، بدون پاسخ، افزودن کاربر، کاربران | فیلتر: ۳ تب',
  guest_user: 'میانبر: تیکت جدید، تیکت‌های من، بدون پاسخ | فیلتر: ۳ تب',
  simple_employee: 'میانبر: تیکت‌ها، ماموریت‌ها، گزارش روزانه | فیلتر: جدید، در انتظار، پاسخ‌داده',
  manager_employee: 'میانبر: تیکت‌ها، ارجاع‌شده، گزارش روزانه، ماموریت‌ها، AI | فیلتر: ۳ تب کوتاه',
  dispatcher: 'میانبر: صف دیسپچر، ارجاع‌شده، شرکت‌ها، کاربران',
};
